import React, { useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import {
    Alert,
    App,
    Button,
    List,
    Modal,
    Result,
    Space,
    Tag,
    Typography,
} from 'antd'
import type { Dayjs } from 'dayjs'
import type { RangePickerProps } from 'antd/es/date-picker'
import type { SortOrder } from 'antd/es/table/interface'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import { PageLayout } from '../components/layout'
import { ReadonlyRangePicker } from '../components/common/DatePickerControls'
import {
    HistoryActions,
    HistoryDetail,
    HistoryList,
} from '../components/history'
import { usePermissions } from '../hooks/usePermissions'
import {
    createHistoryExportJob,
    exportHistoryDataset,
    getCachedHistoryRecord,
    getHistoryRecord,
    getHistoryExportJob,
    getHistoryExportJobDownload,
    listHistoryExportJobs,
    triggerHistoryExportDownload,
    useHistory,
} from '../services/historyService'
import {
    useModelRawClassFilterOptions,
    usePestTypeOptions,
} from '../services/pestTypeOptionsService'
import { localeService } from '../services/localeService'
import {
    HistoryLabelOption,
    HistoryExportJob,
    HistoryListItem,
    HistoryPrediction,
    HistoryRecord,
    HistoryReviewStatus,
} from '../types/history'
import {
    getPredictionDisplayCategory,
    sortMosquitoDisplayCategories,
    type MosquitoDisplayCategory,
} from '../utils/mosquitoDisplay'
import { formatRawClassDisplayName } from '../utils/rawClassDisplay'
import { normalizeSearchText, normalizeSearchValues } from '../utils/searchText'

type HistoryWorkspaceMode = 'history' | 'reviewExport'

const MOCK_REVIEW_EXPORT_ENABLED =
    import.meta.env.DEV && import.meta.env.VITE_MOCK_REVIEW_EXPORT === 'true'
const MOCK_IMAGE_DATA_URL =
    'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="800" height="450" viewBox="0 0 800 450"><rect width="800" height="450" fill="%23f5f5f5"/><rect x="24" y="24" width="752" height="402" fill="%23ffffff" stroke="%23d9d9d9" stroke-width="2"/><text x="50%" y="50%" text-anchor="middle" dominant-baseline="middle" font-family="Arial, sans-serif" font-size="20" fill="%238c8c8c">Mock Image</text></svg>'

const MOCK_LABEL_OPTIONS: HistoryLabelOption[] = [
    {
        cls_id: 0,
        eng_class: 'mosquito',
        class: 'Mosquito',
        display_name: 'Mosquito',
    },
    {
        cls_id: 1,
        eng_class: 'other',
        class: 'Other',
        display_name: 'Other',
    },
]
const DEFAULT_EXPORT_TASKS_PAGE_SIZE = 10
const EXPORT_TASKS_PAGE_SIZE_OPTIONS = ['10', '20', '50']
const EXPORT_MEDIA_RETENTION_DAYS = 90

const summarizePredictions = (
    predictions: HistoryPrediction[] | undefined,
): string | null => {
    if (!predictions?.length) {
        return null
    }

    const counts = predictions.reduce<
        Partial<Record<MosquitoDisplayCategory, number>>
    >((accumulator, prediction) => {
        const category = getPredictionDisplayCategory(prediction)
        if (category) {
            accumulator[category] = (accumulator[category] || 0) + 1
        }
        return accumulator
    }, {})

    const parts = sortMosquitoDisplayCategories(
        Object.keys(counts) as MosquitoDisplayCategory[],
    ).map((category) => `${category}(${counts[category]})`)
    return parts.length > 0 ? parts.join(', ') : null
}

const buildMockHistoryRecords = (): HistoryRecord[] => {
    const now = Date.now()
    return [
        {
            id: 'mock-review-1',
            timestamp: now - 5 * 60 * 1000,
            type: 'image',
            filename: 'mock_review_001.jpg',
            thumbnail: MOCK_IMAGE_DATA_URL,
            device_id: 'DEV-001',
            device_name: 'Mock Device A',
            device_address: 'A1-01',
            region_id: 243,
            region_name: 'Mock Region A',
            result: {
                status: 'ok',
                predictions: [
                    {
                        class: 'Mosquito',
                        eng_class: 'mosquito',
                        cls_id: 0,
                        confidence: 0.82,
                        bbox: { x1: 120, y1: 80, x2: 260, y2: 220 },
                    },
                ],
                source_image: MOCK_IMAGE_DATA_URL,
                annotated_image: MOCK_IMAGE_DATA_URL,
                label_options: MOCK_LABEL_OPTIONS,
                time_cost: 0.86,
            },
            review: {
                status: 'needs_review',
                note: 'Review required',
                predictions: [
                    {
                        class: 'Mosquito',
                        eng_class: 'mosquito',
                        cls_id: 0,
                        confidence: 0.9,
                        bbox: { x1: 118, y1: 82, x2: 258, y2: 218 },
                    },
                ],
            },
        },
        {
            id: 'mock-review-2',
            timestamp: now - 60 * 60 * 1000,
            type: 'image',
            filename: 'mock_review_002.jpg',
            thumbnail: MOCK_IMAGE_DATA_URL,
            device_id: 'DEV-002',
            device_name: 'Mock Device B',
            device_address: 'B2-03',
            region_id: 243,
            region_name: 'Mock Region B',
            result: {
                status: 'ok',
                predictions: [
                    {
                        class: 'Other',
                        eng_class: 'other',
                        cls_id: 1,
                        confidence: 0.64,
                        bbox: { x1: 360, y1: 120, x2: 520, y2: 300 },
                    },
                ],
                source_image: MOCK_IMAGE_DATA_URL,
                annotated_image: MOCK_IMAGE_DATA_URL,
                label_options: MOCK_LABEL_OPTIONS,
                time_cost: 1.12,
            },
            review: {
                status: 'needs_review',
                note: 'Manual review pending',
                predictions: [
                    {
                        class: 'Other',
                        eng_class: 'other',
                        cls_id: 1,
                        confidence: 0.71,
                        bbox: { x1: 362, y1: 122, x2: 518, y2: 298 },
                    },
                ],
            },
        },
    ]
}

const buildMockHistoryListItems = (
    records: HistoryRecord[],
): HistoryListItem[] =>
    records.map((record) => ({
        id: record.id,
        timestamp: record.timestamp,
        type: record.type,
        filename: record.filename,
        device_id: record.device_id,
        device_address: record.device_address,
        device_name: record.device_name,
        current_device_name: record.current_device_name,
        region_id: record.region_id,
        region_name: record.region_name,
        system_summary: summarizePredictions(record.result.predictions),
        review_summary: summarizePredictions(record.review?.predictions),
        review_status: record.review?.status || 'pending',
    }))

interface HistoryWorkspaceProps {
    mode: HistoryWorkspaceMode
}

interface HistoryWorkspaceLocationState {
    refreshHistoryAt?: unknown
}

interface ExportDialogState {
    open: boolean
    dateRange: [Dayjs, Dayjs] | null
}

const isExportDateDisabled: RangePickerProps['disabledDate'] = (current) => {
    if (!current) {
        return false
    }

    const now = dayjs()
    const earliestDate = now.subtract(EXPORT_MEDIA_RETENTION_DAYS, 'day')
    return current.isBefore(earliestDate, 'day') || current.isAfter(now, 'day')
}

const getExportDateRangeErrorKey = (
    dateRange: [Dayjs, Dayjs] | null,
): string | null => {
    if (!dateRange) {
        return null
    }

    const now = dayjs()
    const earliestDateTime = now.subtract(EXPORT_MEDIA_RETENTION_DAYS, 'day')
    const [startDate, endDate] = dateRange
    if (
        startDate.isBefore(earliestDateTime) ||
        endDate.isBefore(earliestDateTime) ||
        startDate.isAfter(now) ||
        endDate.isAfter(now)
    ) {
        return 'history:messages.exportTimeRangeRetentionInvalid'
    }

    return null
}

const getExportJobTagColor = (status: HistoryExportJob['status']): string => {
    switch (status) {
        case 'completed':
            return 'green'
        case 'failed':
            return 'red'
        case 'expired':
            return 'default'
        case 'processing':
            return 'blue'
        default:
            return 'gold'
    }
}

const mapExportErrorMessage = (
    errorMessage: string | null | undefined,
    t: (key: string) => string,
): string => {
    if (!errorMessage) {
        return t('history:messages.exportFailed')
    }

    if (errorMessage === 'No matching history records found') {
        return t('history:messages.exportNoMatchingRecords')
    }

    if (errorMessage === 'No review annotations available for export') {
        return t('history:messages.exportNoReviewAnnotations')
    }

    if (errorMessage === 'No exportable history data found') {
        return t('history:messages.exportNoExportableData')
    }

    if (errorMessage === 'Export task timed out before completion') {
        return t('history:messages.exportTaskTimedOut')
    }

    if (errorMessage === 'History export file has expired') {
        return t('history:messages.exportExpired')
    }

    if (
        errorMessage ===
        'History export task API is not available on the current server'
    ) {
        return t('history:messages.exportTaskApiUnavailable')
    }

    return `${t('history:messages.exportFailed')}: ${errorMessage}`
}

const buildHistoryListItemFromRecord = (
    record: HistoryRecord,
    previousRecord: HistoryListItem,
): HistoryListItem => {
    const effectivePredictions =
        Array.isArray(record.effective_predictions) &&
        record.effective_predictions.length > 0
            ? record.effective_predictions
            : record.effective_source === 'manual'
              ? record.manual_recognition?.predictions
              : record.result.predictions

    return {
        ...previousRecord,
        id: record.id,
        timestamp: record.timestamp,
        type: record.type,
        filename: record.filename,
        thumbnail: record.thumbnail,
        source_image: record.result.source_image,
        device_id: record.device_id,
        device_address: record.device_address,
        device_name: record.device_name,
        current_device_name: record.current_device_name,
        region_id: record.region_id,
        region_name: record.region_name,
        region_path: record.region_path,
        system_summary: summarizePredictions(record.result.predictions),
        review_summary: summarizePredictions(record.review?.predictions),
        effective_summary: summarizePredictions(effectivePredictions),
        manual_type_options: record.manual_type_options,
        effective_source:
            record.effective_source ??
            (record.manual_recognition?.predictions ? 'manual' : 'model'),
        review_status: record.review?.status || previousRecord.review_status,
        package_key_at_upload: record.package_key_at_upload,
        feature_keys_at_upload: record.feature_keys_at_upload,
        media_expired: record.media_expired,
        media_expires_at: record.media_expires_at,
    }
}

export const HistoryWorkspace: React.FC<HistoryWorkspaceProps> = ({ mode }) => {
    const location = useLocation()
    const { message, modal } = App.useApp()
    const { t } = useTranslation(['history', 'common', 'navigation', 'admin'])
    const {
        canDeleteHistory,
        canExportHistoryDataset,
        canReadHistoryDetails,
        canReviewHistory,
    } = usePermissions()
    const isReviewExport = mode === 'reviewExport'
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
    const [deviceNameSearch, setDeviceNameSearch] = useState('')
    const [appliedDeviceNameSearch, setAppliedDeviceNameSearch] = useState('')
    const [pestTypeSearch, setPestTypeSearch] = useState<string[]>([])
    const [appliedPestTypeSearch, setAppliedPestTypeSearch] = useState<
        string[]
    >([])
    const [mockRecords, setMockRecords] = useState<HistoryRecord[]>(() =>
        buildMockHistoryRecords(),
    )
    const [previewVisible, setPreviewVisible] = useState(false)
    const [previewLoading, setPreviewLoading] = useState(false)
    const [previewRecord, setPreviewRecord] = useState<HistoryRecord | null>(
        null,
    )
    const [recordOverrides, setRecordOverrides] = useState<
        Map<string, HistoryListItem>
    >(() => new Map())
    const [previewError, setPreviewError] = useState<string | null>(null)
    const [reviewSubmitting, setReviewSubmitting] = useState(false)
    const [exportSubmitting, setExportSubmitting] = useState(false)
    const [batchExportJobId, setBatchExportJobId] = useState<string | null>(
        null,
    )
    const [exportJobs, setExportJobs] = useState<HistoryExportJob[]>([])
    const [exportJobsLoading, setExportJobsLoading] = useState(false)
    const [exportTasksError, setExportTasksError] = useState<string | null>(
        null,
    )
    const [exportTasksVisible, setExportTasksVisible] = useState(false)
    const [exportTasksPage, setExportTasksPage] = useState(1)
    const [exportTasksPageSize, setExportTasksPageSize] = useState(
        DEFAULT_EXPORT_TASKS_PAGE_SIZE,
    )
    const [queryTrigger, setQueryTrigger] = useState(0)
    const [exportDialog, setExportDialog] = useState<ExportDialogState>({
        open: false,
        dateRange: null,
    })
    const [exportDateRangeErrorKey, setExportDateRangeErrorKey] = useState<
        string | null
    >(null)
    const previewRequestIdRef = useRef(0)
    const previewLoadingIdRef = useRef<string | null>(null)
    const previewTargetRef = useRef<HistoryListItem | null>(null)
    const lastRouteRefreshTokenRef = useRef<unknown>(undefined)
    const { options: historyPestTypeOptions } = usePestTypeOptions(
        [],
        mode === 'history',
    )
    const { options: reviewExportRawClassOptions } =
        useModelRawClassFilterOptions([], isReviewExport)
    const deduplicatedHistoryPestTypeOptions = useMemo(() => {
        const seenLabels = new Set<string>()
        return historyPestTypeOptions.filter((option) => {
            if (seenLabels.has(option.label)) {
                return false
            }
            seenLabels.add(option.label)
            return true
        })
    }, [historyPestTypeOptions])
    const pestTypeOptions = useMemo(
        () =>
            isReviewExport
                ? reviewExportRawClassOptions.map((option) => ({
                      value: option.value,
                      label: formatRawClassDisplayName(
                          option.value,
                          t,
                          option.label,
                      ),
                  }))
                : deduplicatedHistoryPestTypeOptions,
        [
            deduplicatedHistoryPestTypeOptions,
            isReviewExport,
            reviewExportRawClassOptions,
            t,
        ],
    )
    const reviewExportRawClassLocalizedNames = useMemo(
        () =>
            Object.fromEntries(
                reviewExportRawClassOptions.map((option) => [
                    option.value.trim().toLowerCase(),
                    option.label,
                ]),
            ),
        [reviewExportRawClassOptions],
    )

    const {
        deleteRecord,
        records,
        total,
        totalStatus,
        paginationMeta,
        loading,
        error,
        refreshRecords,
        updateReview,
    } = useHistory({
        type: 'image',
        page,
        page_size: pageSize,
        sort_by: isReviewExport ? 'review_updated_at' : 'timestamp',
        sort_order: sortOrder,
        device_name: normalizeSearchText(appliedDeviceNameSearch) || undefined,
        pest_types:
            (mode === 'history' || isReviewExport) &&
            appliedPestTypeSearch.length > 0
                ? appliedPestTypeSearch
                : undefined,
        review_status: isReviewExport ? 'needs_review' : undefined,
        mode: isReviewExport ? 'review_export' : 'history',
        include_review_preview: isReviewExport ? true : undefined,
        forceRefreshKey: queryTrigger,
    })

    React.useEffect(() => {
        setRecordOverrides((currentOverrides) =>
            currentOverrides.size === 0 ? currentOverrides : new Map(),
        )
    }, [records])

    React.useEffect(() => {
        const refreshHistoryAt = (
            location.state as HistoryWorkspaceLocationState | null
        )?.refreshHistoryAt

        if (
            mode !== 'history' ||
            refreshHistoryAt == null ||
            lastRouteRefreshTokenRef.current === refreshHistoryAt
        ) {
            return
        }

        lastRouteRefreshTokenRef.current = refreshHistoryAt
        void refreshRecords(true, { silent: true })
    }, [location.state, mode, refreshRecords])

    const pageTitle = isReviewExport
        ? t('history:reviewExportTitle')
        : t('history:title')
    const pageBreadcrumb = isReviewExport
        ? [{ title: t('navigation:breadcrumb.reviewExport') }]
        : [
              { title: t('navigation:breadcrumb.dataCenter') },
              { title: t('navigation:breadcrumb.history') },
          ]
    const mockListItems = useMemo(
        () => buildMockHistoryListItems(mockRecords),
        [mockRecords],
    )
    const useMockRecords =
        isReviewExport && MOCK_REVIEW_EXPORT_ENABLED && !loading && total === 0
    const visibleRecords = useMemo(() => {
        if (!useMockRecords) {
            if (recordOverrides.size === 0) {
                return records
            }

            return records.map(
                (record) => recordOverrides.get(record.id) ?? record,
            )
        }

        return [...mockListItems].sort((left, right) =>
            sortOrder === 'asc'
                ? left.timestamp - right.timestamp
                : right.timestamp - left.timestamp,
        )
    }, [mockListItems, recordOverrides, records, sortOrder, useMockRecords])
    const visibleTotal = useMockRecords ? mockListItems.length : total
    const editable = isReviewExport ? canReviewHistory : false
    const canBypassHistoryCapability = isReviewExport && canReviewHistory
    const showReviewSummary = isReviewExport
    const canDeleteHistoryRecords =
        mode === 'history' && canDeleteHistory && !useMockRecords
    const canViewHistoryDetails = isReviewExport
        ? canReviewHistory
        : canReadHistoryDetails
    const canExport =
        isReviewExport && canExportHistoryDataset && !useMockRecords
    const isExportBusy = exportSubmitting || batchExportJobId !== null
    const tableSortOrder: SortOrder = sortOrder === 'asc' ? 'ascend' : 'descend'
    const hasActiveExportJobs = exportJobs.some(
        (job) => job.status === 'queued' || job.status === 'processing',
    )
    const paginatedExportJobs = useMemo(() => {
        const offset = (exportTasksPage - 1) * exportTasksPageSize
        return exportJobs.slice(offset, offset + exportTasksPageSize)
    }, [exportJobs, exportTasksPage, exportTasksPageSize])

    React.useEffect(() => {
        const maxPage = Math.max(
            1,
            Math.ceil(exportJobs.length / exportTasksPageSize),
        )
        if (exportTasksPage > maxPage) {
            setExportTasksPage(maxPage)
        }
    }, [exportJobs.length, exportTasksPage, exportTasksPageSize])

    const formatDate = (timestamp: number) =>
        localeService.formatDate(timestamp, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        })

    const formatDateTimeText = (value?: string | null) => {
        if (!value) {
            return t('history:messages.taskTimeUnavailable')
        }

        const parsed = Date.parse(value)
        if (Number.isNaN(parsed)) {
            return value
        }

        return formatDate(parsed)
    }

    const openExportDialog = () => {
        setExportDateRangeErrorKey(null)
        setExportDialog((current) => ({
            ...current,
            open: true,
        }))
    }

    const closeExportDialog = () => {
        setExportDateRangeErrorKey(null)
        setExportDialog((current) => ({
            ...current,
            open: false,
        }))
    }

    const openExportTasks = () => {
        setExportTasksPage(1)
        setExportTasksVisible(true)
        void refreshExportJobs({ notifyOnError: true })
    }

    const handleDeleteRecord = React.useCallback(
        (recordId: string) => {
            modal.confirm({
                title: t('history:modal.confirmDelete'),
                content: t('history:modal.deleteWarning'),
                okText: t('common:buttons.confirm'),
                cancelText: t('common:buttons.cancel'),
                okButtonProps: { danger: true },
                onOk: async () => {
                    const deleted = await deleteRecord(recordId)
                    if (deleted) {
                        if (previewRecord?.id === recordId) {
                            previewRequestIdRef.current += 1
                            setPreviewVisible(false)
                            setPreviewLoading(false)
                            setPreviewRecord(null)
                        }
                        message.success(t('history:messages.deleteSuccess'))
                        return
                    }

                    message.error(t('history:messages.deleteFailed'))
                },
            })
        },
        [deleteRecord, message, modal, previewRecord, t],
    )

    const applyHistorySearch = React.useCallback(() => {
        const nextDeviceNameSearch = normalizeSearchText(deviceNameSearch)
        const nextPestTypeSearch = normalizeSearchValues(pestTypeSearch)
        setPage(1)
        setAppliedDeviceNameSearch(nextDeviceNameSearch)
        setAppliedPestTypeSearch(nextPestTypeSearch)
        setQueryTrigger((value) => value + 1)
    }, [deviceNameSearch, pestTypeSearch])

    const resetHistorySearch = React.useCallback(() => {
        setPage(1)
        setDeviceNameSearch('')
        setAppliedDeviceNameSearch('')
        setPestTypeSearch([])
        setAppliedPestTypeSearch([])
    }, [])

    const closeExportTasks = () => {
        setExportTasksVisible(false)
    }

    const refreshExportJobs = React.useCallback(
        async (options?: { notifyOnError?: boolean }) => {
            if (!isReviewExport || !canExportHistoryDataset || useMockRecords) {
                return
            }

            setExportJobsLoading(true)
            try {
                const result = await listHistoryExportJobs()
                if (result.success) {
                    setExportTasksError(null)
                    setExportJobs(result.jobs)
                    return
                }

                const errorText = mapExportErrorMessage(result.errorMessage, t)
                setExportTasksError(errorText)
                if (options?.notifyOnError) {
                    message.error(errorText)
                }
            } finally {
                setExportJobsLoading(false)
            }
        },
        [canExportHistoryDataset, isReviewExport, message, t, useMockRecords],
    )

    const showDetailModal = async (record: HistoryListItem) => {
        if (previewLoading && previewLoadingIdRef.current === record.id) {
            return
        }
        previewLoadingIdRef.current = record.id
        previewTargetRef.current = record
        setPreviewVisible(true)
        setPreviewError(null)
        previewRequestIdRef.current += 1
        const requestId = previewRequestIdRef.current

        if (useMockRecords) {
            setPreviewRecord(null)
            setPreviewLoading(true)
            const mockRecord =
                mockRecords.find((item) => item.id === record.id) || null
            if (requestId === previewRequestIdRef.current) {
                setPreviewRecord(mockRecord)
                setPreviewError(
                    mockRecord ? null : t('history:messages.detailLoadFailed'),
                )
                setPreviewLoading(false)
                previewLoadingIdRef.current = null
            }
            return
        }

        const cachedRecord = isReviewExport
            ? null
            : getCachedHistoryRecord(record.id)
        if (cachedRecord) {
            setPreviewRecord(cachedRecord)
            setPreviewLoading(false)
            return
        }

        setPreviewRecord(null)
        setPreviewLoading(true)
        const detailRecord = isReviewExport
            ? await getHistoryRecord(record.id, { mode: 'annotation' })
            : await getHistoryRecord(record.id)
        if (requestId === previewRequestIdRef.current) {
            setPreviewRecord(detailRecord)
            setPreviewError(
                detailRecord ? null : t('history:messages.detailLoadFailed'),
            )
            setPreviewLoading(false)
            previewLoadingIdRef.current = null
        }
    }

    const silentlyRefreshVisibleRecord = React.useCallback(
        async (recordId: string) => {
            if (mode !== 'history' || useMockRecords) {
                return
            }

            const previousRecord = records.find(
                (record) => record.id === recordId,
            )
            if (!previousRecord) {
                return
            }

            const refreshedRecord = await getHistoryRecord(recordId).catch(
                () => null,
            )
            if (!refreshedRecord) {
                return
            }

            setRecordOverrides((currentOverrides) => {
                const nextOverrides = new Map(currentOverrides)
                nextOverrides.set(
                    recordId,
                    buildHistoryListItemFromRecord(
                        refreshedRecord,
                        nextOverrides.get(recordId) ?? previousRecord,
                    ),
                )
                return nextOverrides
            })
        },
        [mode, records, useMockRecords],
    )

    const closePreviewModal = React.useCallback(
        (options?: { refreshVisibleRecord?: boolean }) => {
            const recordId = previewRecord?.id ?? previewTargetRef.current?.id
            previewRequestIdRef.current += 1
            setPreviewVisible(false)
            setPreviewLoading(false)
            setPreviewRecord(null)
            setPreviewError(null)
            previewLoadingIdRef.current = null

            if (options?.refreshVisibleRecord && recordId) {
                void silentlyRefreshVisibleRecord(recordId)
            }
        },
        [previewRecord?.id, silentlyRefreshVisibleRecord],
    )

    const updateReviewRecord = async (
        recordId: string,
        payload: {
            status: HistoryReviewStatus
            predictions?: HistoryPrediction[]
            annotation_model_option_keys?: string[]
        },
    ) => {
        if (!useMockRecords) {
            return updateReview(recordId, payload)
        }

        let updated: HistoryRecord | null = null
        setMockRecords((prev) =>
            prev.map((record) => {
                if (record.id !== recordId) {
                    return record
                }
                updated = {
                    ...record,
                    review: {
                        status: payload.status,
                        predictions: payload.predictions,
                        note: record.review?.note ?? null,
                    },
                }
                return updated
            }),
        )
        return updated
    }

    const runExport = async (record?: { id: string }) => {
        if (!canExport || isExportBusy) {
            return
        }

        setExportSubmitting(true)
        try {
            if (!record) {
                const result = await createHistoryExportJob({
                    annotation_source: 'review',
                    review_statuses: ['needs_review'],
                    start_timestamp: exportDialog.dateRange
                        ? exportDialog.dateRange[0].valueOf()
                        : undefined,
                    end_timestamp: exportDialog.dateRange
                        ? exportDialog.dateRange[1].valueOf()
                        : undefined,
                })

                if (result.success && result.job) {
                    const job = result.job
                    setExportTasksError(null)
                    setBatchExportJobId(job.id)
                    setExportJobs((current) => [job, ...current])
                    closeExportDialog()
                    message.success(t('history:messages.exportQueued'))
                } else {
                    setBatchExportJobId(null)
                    message.error(mapExportErrorMessage(result.errorMessage, t))
                }
                return
            }

            const result = await exportHistoryDataset({
                history_ids: record ? [record.id] : undefined,
                annotation_source: 'review',
                review_statuses: ['needs_review'],
                start_timestamp:
                    !record && exportDialog.dateRange
                        ? exportDialog.dateRange[0].valueOf()
                        : undefined,
                end_timestamp:
                    !record && exportDialog.dateRange
                        ? exportDialog.dateRange[1].valueOf()
                        : undefined,
            })
            if (result.success) {
                message.success(t('history:messages.exportSuccess'))
            } else {
                message.error(mapExportErrorMessage(result.errorMessage, t))
            }
        } finally {
            setExportSubmitting(false)
        }
    }

    const executeExport = async () => {
        const errorKey = getExportDateRangeErrorKey(exportDialog.dateRange)
        setExportDateRangeErrorKey(errorKey)
        if (errorKey) {
            return
        }

        await runExport()
    }

    const handleExportDateRangeChange = React.useCallback<
        NonNullable<RangePickerProps['onChange']>
    >((dates) => {
        const nextDateRange =
            dates && dates[0] && dates[1]
                ? ([dates[0], dates[1]] as [Dayjs, Dayjs])
                : null
        setExportDateRangeErrorKey(getExportDateRangeErrorKey(nextDateRange))
        setExportDialog((current) => ({
            ...current,
            dateRange: nextDateRange,
        }))
    }, [])

    const handleExportJobDownload = React.useCallback(
        async (jobId: string) => {
            const result = await getHistoryExportJobDownload(jobId)
            if (!result.success || !result.download) {
                message.error(
                    result.errorMessage
                        ? mapExportErrorMessage(result.errorMessage, t)
                        : t('history:messages.exportDownloadFailed'),
                )
                return
            }
            triggerHistoryExportDownload(result.download)
            message.success(t('history:messages.exportReady'))
        },
        [message, t],
    )

    React.useEffect(() => {
        void refreshExportJobs()
    }, [refreshExportJobs])

    React.useEffect(() => {
        if (!batchExportJobId) {
            return
        }

        let cancelled = false

        const pollExportJob = async () => {
            const result = await getHistoryExportJob(batchExportJobId)
            if (cancelled) {
                return
            }
            if (!result.success || !result.job) {
                setBatchExportJobId(null)
                void refreshExportJobs()
                message.error(
                    result.errorMessage
                        ? mapExportErrorMessage(result.errorMessage, t)
                        : t('history:messages.exportTaskStatusFailed'),
                )
                return
            }
            const job = result.job

            setExportJobs((current) => {
                const next = current.filter((item) => item.id !== job.id)
                return [job, ...next]
            })

            if (job.status === 'queued' || job.status === 'processing') {
                return
            }

            setBatchExportJobId(null)
            if (job.status === 'completed' && job.download_ready) {
                const downloadResult = await getHistoryExportJobDownload(job.id)
                if (!downloadResult.success || !downloadResult.download) {
                    message.error(
                        downloadResult.errorMessage
                            ? mapExportErrorMessage(
                                  downloadResult.errorMessage,
                                  t,
                              )
                            : t('history:messages.exportDownloadFailed'),
                    )
                    return
                }
                triggerHistoryExportDownload(downloadResult.download)
                message.success(t('history:messages.exportReady'))
                return
            }

            if (job.status === 'expired') {
                message.error(t('history:messages.exportExpired'))
                return
            }

            message.error(mapExportErrorMessage(job.error_message, t))
        }

        void pollExportJob()
        const timer = window.setInterval(() => {
            void pollExportJob()
        }, 2500)

        return () => {
            cancelled = true
            window.clearInterval(timer)
        }
    }, [batchExportJobId, message, refreshExportJobs, t])

    React.useEffect(() => {
        if (
            !isReviewExport ||
            !canExportHistoryDataset ||
            useMockRecords ||
            (!hasActiveExportJobs && !batchExportJobId)
        ) {
            return
        }

        const timer = window.setInterval(() => {
            void refreshExportJobs()
        }, 5000)

        return () => {
            window.clearInterval(timer)
        }
    }, [
        batchExportJobId,
        canExportHistoryDataset,
        hasActiveExportJobs,
        isReviewExport,
        refreshExportJobs,
        useMockRecords,
    ])

    if (isReviewExport && !canExportHistoryDataset) {
        return (
            <PageLayout title={pageTitle} breadcrumb={pageBreadcrumb}>
                <Result
                    status="403"
                    title={t('common:featureNotAvailable')}
                    subTitle={t('history:messages.reviewExportAccessDenied')}
                    extra={
                        <Button type="primary">
                            <Link to="/history">{t('history:title')}</Link>
                        </Button>
                    }
                />
            </PageLayout>
        )
    }

    return (
        <PageLayout title={pageTitle} breadcrumb={pageBreadcrumb}>
            <HistoryActions
                hasRecords={visibleRecords.length > 0}
                loading={loading}
                canExport={canExport}
                canViewExportTasks={isReviewExport && canExport}
                exporting={isExportBusy}
                deviceNameSearchValue={deviceNameSearch}
                pestTypeOptions={pestTypeOptions}
                pestTypeSearchValue={pestTypeSearch}
                showDeviceNameSearch={mode === 'history' || isReviewExport}
                showPestTypeSearch={mode === 'history' || isReviewExport}
                onExport={openExportDialog}
                onViewExportTasks={openExportTasks}
                onDeviceNameSearch={applyHistorySearch}
                onDeviceNameSearchChange={setDeviceNameSearch}
                onDeviceNameSearchReset={resetHistorySearch}
                onPestTypeSearchChange={setPestTypeSearch}
            />

            {error && visibleRecords.length === 0 && !loading ? (
                <Result
                    status="warning"
                    title={t('history:messages.listLoadFailed')}
                    extra={
                        <Button onClick={() => refreshRecords(true)}>
                            {t('history:actions.refresh')}
                        </Button>
                    }
                />
            ) : (
                <HistoryList
                    records={visibleRecords}
                    total={visibleTotal}
                    totalStatus={useMockRecords ? 'ready' : totalStatus}
                    paginationMeta={
                        useMockRecords
                            ? {
                                  total_pages: Math.ceil(
                                      visibleTotal / Math.max(1, pageSize),
                                  ),
                                  page_depth_limit: 0,
                                  max_accessible_page: Math.max(
                                      0,
                                      Math.ceil(
                                          visibleTotal / Math.max(1, pageSize),
                                      ),
                                  ),
                                  pagination_limited: false,
                              }
                            : paginationMeta
                    }
                    currentPage={page}
                    pageSize={pageSize}
                    loading={loading}
                    emptyDescription={
                        isReviewExport
                            ? t('history:messages.noReviewExportRecords')
                            : undefined
                    }
                    editable={editable}
                    showReviewSummary={showReviewSummary}
                    showReviewUpdatedAtColumn={isReviewExport}
                    bypassCapabilityFallback={canBypassHistoryCapability}
                    showViewAction={canViewHistoryDetails}
                    showExportAction={canExport}
                    showDeleteAction={canDeleteHistoryRecords}
                    onDelete={
                        canDeleteHistoryRecords ? handleDeleteRecord : undefined
                    }
                    onView={canViewHistoryDetails ? showDetailModal : undefined}
                    onExport={canExport ? runExport : undefined}
                    onPageChange={(nextPage, nextPageSize) => {
                        setPage(nextPage)
                        setPageSize(nextPageSize)
                    }}
                    onSortChange={(nextSortOrder) => {
                        setPage(1)
                        setSortOrder(nextSortOrder)
                    }}
                    sortOrder={tableSortOrder}
                    formatDate={formatDate}
                    rawClassLocalizedNames={
                        isReviewExport
                            ? reviewExportRawClassLocalizedNames
                            : undefined
                    }
                />
            )}

            {isReviewExport ? (
                <HistoryDetail
                    visible={previewVisible}
                    record={previewRecord}
                    rawClassLocalizedNames={reviewExportRawClassLocalizedNames}
                    loading={previewLoading}
                    error={previewError}
                    editable={editable}
                    canExport={canExport}
                    bypassCapabilityFallback={canBypassHistoryCapability}
                    reviewSubmitting={reviewSubmitting}
                    exportSubmitting={isExportBusy}
                    onClose={() => closePreviewModal()}
                    onRetry={() => {
                        const target = previewTargetRef.current
                        if (target) {
                            void showDetailModal(target)
                        }
                    }}
                    onUpdateReview={async (
                        status: HistoryReviewStatus,
                        predictions?: HistoryPrediction[],
                        annotationModelOptionKeys?: string[],
                    ) => {
                        if (!previewRecord) {
                            return null
                        }

                        setReviewSubmitting(true)
                        try {
                            const updatedRecord = await updateReviewRecord(
                                previewRecord.id,
                                {
                                    status,
                                    predictions,
                                    annotation_model_option_keys:
                                        annotationModelOptionKeys,
                                },
                            )
                            if (updatedRecord) {
                                setPreviewRecord(updatedRecord)
                            }
                            return updatedRecord
                        } finally {
                            setReviewSubmitting(false)
                        }
                    }}
                    onExport={canExport ? runExport : async () => undefined}
                    formatDate={formatDate}
                    titleKey="actions.viewDetails"
                    showReviewComparisonTabs
                    reviewComparisonDefaultActiveKey="review"
                    showReviewComparisonSystemDetectionInfo={false}
                    showAnnotationTypeLegend
                />
            ) : (
                <HistoryDetail
                    visible={previewVisible}
                    record={previewRecord}
                    loading={previewLoading}
                    error={previewError}
                    editable={editable}
                    canExport={canExport}
                    bypassCapabilityFallback={canBypassHistoryCapability}
                    readonlyPredictionSource={
                        mode === 'history' ? 'effective' : undefined
                    }
                    readonlyPreviewOnImageClick={mode === 'history'}
                    showAnnotationLabelsOnBoxes={false}
                    showAnnotationTypeLegend={mode === 'history'}
                    reviewSubmitting={reviewSubmitting}
                    exportSubmitting={isExportBusy}
                    onClose={() =>
                        closePreviewModal({ refreshVisibleRecord: true })
                    }
                    onRetry={() => {
                        const target = previewTargetRef.current
                        if (target) {
                            void showDetailModal(target)
                        }
                    }}
                    onUpdateReview={async (
                        status: HistoryReviewStatus,
                        predictions?: HistoryPrediction[],
                        annotationModelOptionKeys?: string[],
                    ) => {
                        if (!previewRecord) {
                            return null
                        }

                        setReviewSubmitting(true)
                        try {
                            const updatedRecord = await updateReviewRecord(
                                previewRecord.id,
                                {
                                    status,
                                    predictions,
                                    annotation_model_option_keys:
                                        annotationModelOptionKeys,
                                },
                            )
                            if (updatedRecord) {
                                setPreviewRecord(updatedRecord)
                            }
                            return updatedRecord
                        } finally {
                            setReviewSubmitting(false)
                        }
                    }}
                    onExport={canExport ? runExport : async () => undefined}
                    formatDate={formatDate}
                />
            )}

            {canExport && (
                <Modal
                    title={t('history:modal.exportSourceTitle')}
                    open={exportDialog.open}
                    onOk={executeExport}
                    onCancel={closeExportDialog}
                    confirmLoading={isExportBusy}
                    maskClosable={false}
                >
                    <Space
                        direction="vertical"
                        size={12}
                        style={{ width: '100%' }}
                    >
                        <Typography.Text type="secondary">
                            {t('history:modal.exportSourceHintAll')}
                        </Typography.Text>
                        <Space
                            direction="vertical"
                            size={8}
                            style={{ width: '100%' }}
                        >
                            <Typography.Text strong>
                                {t('history:modal.exportTimeRangeTitle')}
                            </Typography.Text>
                            <Typography.Text type="secondary">
                                {t('history:modal.exportTimeRangeHint')}
                            </Typography.Text>
                            <ReadonlyRangePicker
                                showTime
                                value={exportDialog.dateRange}
                                onChange={handleExportDateRangeChange}
                                disabledDate={isExportDateDisabled}
                                status={
                                    exportDateRangeErrorKey
                                        ? 'error'
                                        : undefined
                                }
                                style={{ width: '100%' }}
                                format="YYYY-MM-DD HH:mm:ss"
                                placeholder={[
                                    t(
                                        'history:modal.exportTimeRangePlaceholderStart',
                                    ),
                                    t(
                                        'history:modal.exportTimeRangePlaceholderEnd',
                                    ),
                                ]}
                            />
                            {exportDateRangeErrorKey && (
                                <Typography.Text type="danger" role="alert">
                                    {t(exportDateRangeErrorKey)}
                                </Typography.Text>
                            )}
                        </Space>
                    </Space>
                </Modal>
            )}

            {isReviewExport && canExport && (
                <Modal
                    title={t('history:tasks.title')}
                    open={exportTasksVisible}
                    onCancel={closeExportTasks}
                    footer={[
                        <Button
                            key="refresh"
                            onClick={() => {
                                void refreshExportJobs()
                            }}
                            loading={exportJobsLoading}
                        >
                            {t('history:actions.refresh')}
                        </Button>,
                        <Button key="close" onClick={closeExportTasks}>
                            {t('history:actions.close')}
                        </Button>,
                    ]}
                    width={860}
                >
                    <Space
                        direction="vertical"
                        size={12}
                        style={{ width: '100%' }}
                    >
                        <Alert
                            type="info"
                            showIcon
                            message={t('history:tasks.hint')}
                        />
                        {exportTasksError && (
                            <Alert
                                type="error"
                                showIcon
                                message={exportTasksError}
                            />
                        )}
                        <List
                            loading={exportJobsLoading}
                            locale={{ emptyText: t('history:tasks.empty') }}
                            dataSource={paginatedExportJobs}
                            pagination={{
                                current: exportTasksPage,
                                pageSize: exportTasksPageSize,
                                total: exportJobs.length,
                                showSizeChanger: true,
                                pageSizeOptions: EXPORT_TASKS_PAGE_SIZE_OPTIONS,
                                onChange: (nextPage, nextPageSize) => {
                                    if (
                                        nextPageSize &&
                                        nextPageSize !== exportTasksPageSize
                                    ) {
                                        setExportTasksPageSize(nextPageSize)
                                        setExportTasksPage(1)
                                        return
                                    }
                                    setExportTasksPage(nextPage)
                                },
                            }}
                            renderItem={(job) => (
                                <List.Item
                                    actions={[
                                        ...(job.download_ready
                                            ? [
                                                  <Button
                                                      key={`download-${job.id}`}
                                                      size="small"
                                                      onClick={() => {
                                                          void handleExportJobDownload(
                                                              job.id,
                                                          )
                                                      }}
                                                  >
                                                      {t(
                                                          'history:actions.download',
                                                      )}
                                                  </Button>,
                                              ]
                                            : []),
                                    ]}
                                >
                                    <Space
                                        direction="vertical"
                                        size={4}
                                        style={{ width: '100%' }}
                                    >
                                        <Space wrap>
                                            <Typography.Text strong>
                                                {job.file_name ||
                                                    t(
                                                        'history:tasks.unnamedFile',
                                                    )}
                                            </Typography.Text>
                                            <Tag
                                                color={getExportJobTagColor(
                                                    job.status,
                                                )}
                                            >
                                                {t(
                                                    `history:tasks.status.${job.status}`,
                                                )}
                                            </Tag>
                                        </Space>
                                        <Typography.Text type="secondary">
                                            {t('history:tasks.createdAt')}:{' '}
                                            {formatDateTimeText(job.created_at)}
                                        </Typography.Text>
                                        <Typography.Text type="secondary">
                                            {t('history:tasks.expiresAt')}:{' '}
                                            {formatDateTimeText(job.expires_at)}
                                        </Typography.Text>
                                        {job.error_message && (
                                            <Typography.Text type="danger">
                                                {mapExportErrorMessage(
                                                    job.error_message,
                                                    t,
                                                )}
                                            </Typography.Text>
                                        )}
                                    </Space>
                                </List.Item>
                            )}
                        />
                    </Space>
                </Modal>
            )}
        </PageLayout>
    )
}

const HistoryWorkspacePage: React.FC<HistoryWorkspaceProps> = ({ mode }) => (
    <HistoryWorkspace mode={mode} />
)

export default HistoryWorkspacePage
